/* eslint-disable @typescript-eslint/no-extra-parens */
import { Content, ContentType, NotFoundError, sentryLogger, XcoreConfig, XcoreCore } from "@robe/core";
import { GetStaticPropsResult } from "next";
import { GetXcoreServerContext, GetXcoreStaticContext, XcoreProps, XcorePropsOptions } from "./types";

/**
 * Asynchronously fetch all data provided in `getXcoreProps` or `getXcoreServerProps`.
 *
 * @template PageContent - The type of page content.
 * @template ContentSingletons - The type of the array of content singletons.
 * @template MultipleContents - The type of the array of multiple contents.
 * @template Data - The type of the data.
 *
 * @param {XcoreConfig} config Xcore config.
 * @param {Awaitable<XcorePropsOptions<PageContent, ContentSingletons, MultipleContents, Data>>} options Definition of what data should be fetched on initial page load.
 */
export const resolveOptions =
  <PageContent extends Content, ContentSingletons extends Content[], MultipleContents extends Content[], Data>(
    config: XcoreConfig,
    options: XcorePropsOptions<PageContent, ContentSingletons, MultipleContents, Data>
  ) =>
  async ({
    core,
  }: GetXcoreStaticContext | GetXcoreServerContext): Promise<
    GetStaticPropsResult<XcoreProps<PageContent, ContentSingletons, MultipleContents, Data>>
  > => {
    try {
      const [contentTypes, pageContent, contentSingletons, multipleContents, subCategories, reverseRelations, data] = await Promise.all([
        // Needed in layout because CT routes are needed in content links initialization and it cannot be known which CT user will choose for content link
        core.contentType.all({ minimal: true }),
        resolvePageContent(core, options.pageContent),
        Promise.all(options?.contentSingletons ?? []),
        Promise.all(options?.multipleContents ?? []),
        Promise.all(options?.subCategories ?? []),
        Promise.all(options?.reverseRelations ?? []),
        options?.data?.(),
      ]);

      // If there is `null` instead of content object consider it as missing in res
      const missingFetchedContentSingletons = options?.contentSingletons?.filter((c) => c === null);
      const missingFetchedMultipleContents = options?.multipleContents?.filter((c) => c === null);

      // If page content or any other contents that should be fetched in props are null return `404`
      if (
        !pageContent ||
        (missingFetchedContentSingletons && missingFetchedContentSingletons.length > 0) ||
        (missingFetchedMultipleContents && missingFetchedMultipleContents.length > 0)
      ) {
        sentryLogger({
          type: "debug",
          debugBlock: "routing",
          message: "Requested content for page not found, redirecting to 404",
          context: {
            ...(options.pageContent &&
              (typeof options.pageContent === "string" || typeof options.pageContent === "number") && {
                page: options.pageContent,
              }),
            pageContent,
            missingFetchedContentSingletons: missingFetchedContentSingletons && missingFetchedContentSingletons.length > 0,
            missingFetchedMultipleContents: missingFetchedMultipleContents && missingFetchedMultipleContents.length > 0,
          },
        });

        return {
          notFound: true,
        };
      }

      return {
        props: {
          _xcoreProps: true,
          /**
           * This is being used in content link to make sure that ct is routable and to resolve content route.
           * Also available via `useLayout()` on web and used in some places
           */
          contentTypes: mapContentTypes(contentTypes),
          reverseRelations,
          pageContent,
          pageContentPublishedLocales: await resolvePageContentPublishedLocales(core, pageContent),
          contentSingletons: contentSingletons as any,
          // todo xcc any
          multipleContents: multipleContents as any,
          subCategories: subCategories,
          data: (data ?? {}) as Data,
          ctx: core.getSerializableCtx(),
          config: config.value,
        },
        /** `0` to skip cache */
        ...(options?.revalidate && {
          revalidate: options.revalidate,
        }),
      };
    } catch (error) {
      if (error instanceof NotFoundError) {
        return {
          notFound: true,
        };
      }

      sentryLogger({
        error,
        message: "Unknown error occurred in getXcoreProps",
        ...((typeof options.pageContent === "string" || typeof options.pageContent === "number") && {
          context: { pageContent: options.pageContent },
        }),
      });

      throw error;
    }
  };

const mapContentTypes = (contentTypes: ContentType[]) => Object.fromEntries(contentTypes.map(({ id, ...rest }) => [id, { id, ...rest }]));

const resolvePageContentPublishedLocales = async <PageContent extends Content>(
  core: XcoreCore,
  pageContent: Awaited<PageContent> | null
) => {
  if (!pageContent) {
    return [];
  }

  const pageContentWithAllLocales = await core.content.singleById(pageContent?.id, { locales: [] });

  if (!pageContentWithAllLocales?.publishedAt || pageContentWithAllLocales?.publishedAt?.value) {
    return [];
  }

  return Object.keys(pageContentWithAllLocales.publishedAt);
};

const resolvePageContent = async <PageContent extends Content>(
  core: XcoreCore,
  pageContent: XcorePropsOptions<PageContent>["pageContent"]
) => {
  if (typeof pageContent === "string") {
    return await core.content.singletonByType<PageContent>(pageContent, { includeValues: true });
  }

  if (typeof pageContent === "number") {
    return await core.content.singleById<PageContent>(pageContent, { includeValues: true });
  }

  return await pageContent;
};
